import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { InventoryOutputModel } from 'src/app/models/inventory/inventory-output.model';
import { RemissionFromOrderDetailModel } from 'src/app/models/rental/remission-from-order-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { SalesRemissionFromOrderService } from 'src/app/services/inventory/sales-remission-from-order.service';
import { OrderDetailService } from 'src/app/services/preliminaries/order-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-sales-remission-manual-order',
  templateUrl: './sales-remission-manual-order.component.html',
  styleUrls: ['./sales-remission-manual-order.component.scss'],
  standalone: false
})
export class SalesRemissionManualOrderComponent implements OnInit {

  private readonly orderDetailService = inject(OrderDetailService);
  private readonly salesRemissionFromOrderService = inject(SalesRemissionFromOrderService);
  readonly config = inject(DynamicDialogConfig);
  private readonly toastHandlerService = inject(ToastHandlerService);

  articleId: number = 0;
  ascendantArticle: boolean = true;
  ascendantStoreStock: boolean = true;
  columnSortArticle: string | null = null;
  columnSortStoreStock: string | null = null;
  currentStep: number = 1;
  dataOrderArticle: PagedResponseModel<RemissionFromOrderDetailModel> =
    new PagedResponseModel<RemissionFromOrderDetailModel>();
  dataStoreStock: PagedResponseModel<RemissionFromOrderDetailModel> =
    new PagedResponseModel<RemissionFromOrderDetailModel>();
  filtersArticle: ColumnResponseModel[] = [];
  filtersStoreStock: ColumnResponseModel[] = [];
  headerOrderArticle: ColumnResponseModel[] = [];
  headerStoreStock: ColumnResponseModel[] = [];
  limitArticle: number = 10;
  limitStoreStock: number = 10;
  loading: boolean = false;
  pageArticle: number = 1;
  pageStoreStock: number = 1;
  salesRemissionModel: InventoryOutputModel = new InventoryOutputModel();
  readonly stepPanel = StepPanelEnums;
  subscription: Subscription;

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.salesRemissionModel = inputs.salesRemissionModel;
    });
    this.loading = true;
    await this.getDataForStep(StepPanelEnums.PanelOne);
    setTimeout(() => this.loading = false, 300);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async getArticlesByOrderForSelection() {
    try {
      const response = await firstValueFrom(this.salesRemissionFromOrderService.getArticlesByOrderForSelection(
        this.salesRemissionModel.inventoryOutputId, this.pageArticle, this.limitArticle, this.columnSortArticle,
        this.ascendantArticle, this.filtersArticle));
      this.dataOrderArticle = response;
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getColumnsForArticleByOrder() {
    try {
      if (this.headerOrderArticle.length == 0) {
        const response = await firstValueFrom(this.salesRemissionFromOrderService.getColumnsForArticleByOrder());
        this.headerOrderArticle = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getColumnsForStoreStockByArticle() {
    try {
      if (this.headerStoreStock.length === 0) {
        const response = await firstValueFrom(this.salesRemissionFromOrderService.getColumnsForStoreStockByArticle());
        this.headerStoreStock = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para seleccionar el artículo a remisionar.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    try {
      const actions: Record<number, () => Promise<any>> = {
        [StepPanelEnums.PanelOne]: () => Promise.all([
          this.getColumnsForArticleByOrder(),
          this.getArticlesByOrderForSelection()
        ]),
        [StepPanelEnums.PanelTwo]: () => Promise.all([
          this.getColumnsForStoreStockByArticle(),
          this.getStoreStockByArticleForSelection()
        ])
      };
      if (actions[step]) {
        await actions[step]();
      }
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async getOrderDetailById(orderDetailId: number | null) {
    try {
      if (orderDetailId) {
        const orderDetail = await firstValueFrom(this.orderDetailService.getOrderDetailById(orderDetailId));
        this.articleId = orderDetail.articleId;
      } else {
        this.articleId = 0;
      }
    } catch (error) {
      this.articleId = 0;
      console.error('Error al obtener la cotización.', error);
    }
  }

  async getStoreStockByArticleForSelection() {
    try {
      const response = await firstValueFrom(this.salesRemissionFromOrderService.getStoreStockByArticleForSelection(
        this.salesRemissionModel.inventoryOutputId, this.articleId, this.pageStoreStock, this.limitStoreStock,
        this.columnSortStoreStock, this.ascendantStoreStock, this.filtersStoreStock
      ));
      this.dataStoreStock = response;
    } catch (error) {
      console.error('Error al obtener los artículos a remisionar.', error);
    }
  }

  async goToStep(step: StepPanelEnums, activateCallback: (step: number) => void) {
    if (step > StepPanelEnums.PanelOne && this.articleId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Por favor, seleccione un artículo válido.'
      );
      return;
    }
    if (step === StepPanelEnums.PanelOne) {
      this.articleId = 0;
    }
    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }

  async onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    const cleanFilters = this.removeDuplicates(filters);
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.filtersArticle = cleanFilters;
        this.filtersArticle.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelOne));
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.filtersStoreStock = cleanFilters;
        this.filtersStoreStock.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    switch (this.currentStep) {
      case 1:
        this.pageArticle = event.page;
        this.limitArticle = event.pageSize;
        this.columnSortArticle = event.sortField || null;
        this.ascendantArticle = event.sortOrder === 1;
        await this.getArticlesByOrderForSelection()
        break;
      case 2:
        this.pageStoreStock = event.page;
        this.limitStoreStock = event.pageSize;
        this.columnSortStoreStock = event.sortField || null;
        this.ascendantStoreStock = event.sortOrder === 1;
        await this.getStoreStockByArticleForSelection()
        break;
    }
  }

  processFilter(filter: any, step: StepPanelEnums) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    const isInvalid = valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0;
    if (isInvalid) {
      if (step == StepPanelEnums.PanelOne) {
        this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
      } else if (step == StepPanelEnums.PanelTwo) {
        this.filtersStoreStock = this.filtersStoreStock.filter(f => f.propertyName !== propertyName);
      }
    } else {
      const list = step === StepPanelEnums.PanelOne ? this.filtersArticle : this.filtersStoreStock;
      const existingFilter = list.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async selectionChange(event: { selectedItem?: any }) {
    const selectedId = event?.selectedItem?.orderDetailId;
    await this.getOrderDetailById(selectedId);
  }
}

